import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Newspaper, ArrowLeft, Plus, Search, FileDown, BarChart3, Globe, Calendar, User, AlertTriangle, Sparkles, TrendingUp, Zap, Brain } from 'lucide-react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { useAuth } from '../context/AuthContext';
import { htmlToPlainText } from '../utils/htmlToPlainText';
import { parseLocalDate, formatLocalDate } from '../utils/dateHelpers';
import WebScraping from './WebScraping';
import FormularioGestion from './FormularioGestion';
import FormularioGestionPrelleno from './FormularioGestionPrelleno';
import AccionesGestion from './AccionesGestion';
import DetalleGestion from './DetalleGestion';
import EditarGestion from './EditarGestion';
import EstadisticasModulo from './EstadisticasModulo';
import './ModuloBase.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ModuloMonitoreoMedios = ({ onBack }) => {
  const { user, canCreateCases, canEditCases, isAnalyst, canViewGestion } = useAuth();
  const [view, setView] = useState('list');
  const [gestiones, setGestiones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedGestion, setSelectedGestion] = useState(null);
  const [datosPrellenos, setDatosPrellenos] = useState(null);
  
  // Catálogos para filtros
  const [instituciones, setInstituciones] = useState([]);
  const [asesoresCNC, setAsesoresCNC] = useState([]);
  const [redesSociales, setRedesSociales] = useState([]);
  const [cuentasSociales, setCuentasSociales] = useState([]);
  
  const [filtros, setFiltros] = useState({
    q: '',
    estado: '',
    red_social: '',
    institucion: '',
    asesor_cnc: '',
    fecha_asignacion_desde: '',
    fecha_asignacion_hasta: '',
    fecha_vencimiento_cnc_desde: '',
    fecha_vencimiento_cnc_hasta: '',
    fecha_vencimiento_probidad_desde: '',
    fecha_vencimiento_probidad_hasta: ''
  });

  useEffect(() => {
    fetchGestiones();
    fetchCatalogos();
  }, []);
  
  const fetchCatalogos = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [instResponse, asesoresResponse, redesResponse, cuentasResponse] = await Promise.all([
        axios.get(`${API}/catalogos/instituciones`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/catalogos/asesores-cnc?activo=true`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/catalogs/networks`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/catalogs/cuentas_sociales`, { headers }).catch(() => ({ data: [] }))
      ]);
      
      setInstituciones(instResponse.data || []);
      setAsesoresCNC(asesoresResponse.data || []);
      setRedesSociales((redesResponse.data || []).filter(r => r.is_active !== false));
      setCuentasSociales((cuentasResponse.data || []).filter(c => c.is_active !== false));
    } catch (error) {
      console.error('Error al cargar catálogos:', error);
    }
  };

  const fetchGestiones = async () => {
    setLoading(true);
    try {
      // Buscar con ambos nombres para compatibilidad con datos existentes
      const [response1, response2] = await Promise.all([
        axios.get(`${API}/gestiones?tipo_gestion=Monitoreo`).catch(() => ({ data: [] })),
        axios.get(`${API}/gestiones?tipo_gestion=Monitoreo de Medios`).catch(() => ({ data: [] }))
      ]);
      let allGestiones = [...(response1.data || []), ...(response2.data || [])];
      // Eliminar duplicados por ID
      let uniqueGestiones = allGestiones.filter((g, index, self) => 
        index === self.findIndex(t => t.id === g.id)
      );
      // Si es analista, filtrar solo sus gestiones
      if (isAnalyst()) {
        uniqueGestiones = uniqueGestiones.filter(g => canViewGestion(g));
      }
      setGestiones(uniqueGestiones);
    } catch (error) {
      console.error('Error al cargar gestiones:', error);
      toast.error('Error al cargar las gestiones');
    } finally {
      setLoading(false);
    }
  };

  const handleExportarExcel = () => {
    if (gestiones.length === 0) {
      toast.error('No hay datos para exportar');
      return;
    }

    const data = gestiones.map(g => ({
      'Nomenclatura': g.nomenclatura,
      'Fecha': formatLocalDate(g.fecha_asignacion),
      'Autor': g.campos_especificos?.author || 'N/A',
      'Red Social': g.campos_especificos?.network || 'N/A',
      'Personas Mencionadas': g.campos_especificos?.persons || 'N/A',
      'Institución': g.institucion,
      'Estado': g.estado,
      'Asesor CNC': g.nombre_asesor_cnc,
      'Descripción': htmlToPlainText(g.descripcion),
      'Enlaces Referenciales': (() => {
        const enlaces = g.campos_especificos?.enlaces || [];
        const linkPrincipal = g.campos_especificos?.link;
        const todosEnlaces = enlaces.length > 0 ? enlaces : (linkPrincipal ? [linkPrincipal] : []);
        return todosEnlaces.filter(Boolean).join('\n') || 'N/A';
      })()
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Monitoreo Medios');
    
    const fecha = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `Monitoreo_Medios_${fecha}.xlsx`);
    toast.success('Excel exportado correctamente');
  };

  const handleBuscar = () => {
    let result = [...gestiones];
    
    // Búsqueda por texto
    if (filtros.q) {
      const q = filtros.q.toLowerCase();
      result = result.filter(g => 
        g.nomenclatura?.toLowerCase().includes(q) ||
        g.descripcion?.toLowerCase().includes(q) ||
        g.institucion?.toLowerCase().includes(q) ||
        g.nombre_asesor_cnc?.toLowerCase().includes(q) ||
        g.campos_especificos?.author?.toLowerCase().includes(q) ||
        g.campos_especificos?.persons?.toLowerCase().includes(q)
      );
    }
    
    // Filtro por estado
    if (filtros.estado) {
      result = result.filter(g => g.estado === filtros.estado);
    }
    
    // Filtro por red social
    if (filtros.red_social) {
      result = result.filter(g => g.campos_especificos?.network === filtros.red_social);
    }
    
    // Filtro por institución
    if (filtros.institucion) {
      result = result.filter(g => g.institucion === filtros.institucion);
    }
    
    // Filtro por asesor CNC
    if (filtros.asesor_cnc) {
      result = result.filter(g => g.nombre_asesor_cnc === filtros.asesor_cnc);
    }
    
    // Filtro por fecha de asignación CNC
    if (filtros.fecha_asignacion_desde) {
      result = result.filter(g => g.fecha_asignacion && new Date(g.fecha_asignacion) >= new Date(filtros.fecha_asignacion_desde));
    }
    if (filtros.fecha_asignacion_hasta) {
      result = result.filter(g => g.fecha_asignacion && new Date(g.fecha_asignacion) <= new Date(filtros.fecha_asignacion_hasta));
    }
    
    // Filtro por fecha de vencimiento CNC
    if (filtros.fecha_vencimiento_cnc_desde) {
      result = result.filter(g => g.fecha_vencimiento && new Date(g.fecha_vencimiento) >= new Date(filtros.fecha_vencimiento_cnc_desde));
    }
    if (filtros.fecha_vencimiento_cnc_hasta) {
      result = result.filter(g => g.fecha_vencimiento && new Date(g.fecha_vencimiento) <= new Date(filtros.fecha_vencimiento_cnc_hasta));
    }
    
    // Filtro por fecha de vencimiento Instancia Probidad
    if (filtros.fecha_vencimiento_probidad_desde) {
      result = result.filter(g => g.fecha_vencimiento_probidad && new Date(g.fecha_vencimiento_probidad) >= new Date(filtros.fecha_vencimiento_probidad_desde));
    }
    if (filtros.fecha_vencimiento_probidad_hasta) {
      result = result.filter(g => g.fecha_vencimiento_probidad && new Date(g.fecha_vencimiento_probidad) <= new Date(filtros.fecha_vencimiento_probidad_hasta));
    }
    
    setGestiones(result);
    toast.success(`${result.length} gestiones encontradas`);
  };

  const handleLimpiarFiltros = () => {
    setFiltros({
      q: '',
      estado: '',
      red_social: '',
      institucion: '',
      asesor_cnc: '',
      fecha_asignacion_desde: '',
      fecha_asignacion_hasta: '',
      fecha_vencimiento_cnc_desde: '',
      fecha_vencimiento_cnc_hasta: '',
      fecha_vencimiento_probidad_desde: '',
      fecha_vencimiento_probidad_hasta: ''
    });
    fetchGestiones();
  };

  const handleViewDetail = (gestion) => {
    setSelectedGestion(gestion);
    setView('detail');
  };

  const handleEditGestion = (gestion) => {
    setSelectedGestion(gestion);
    setView('edit');
  };

  const getRedSocialBadge = (network) => {
    const colors = {
      'X': '#000000',
      'Facebook': '#1877F2',
      'Instagram': '#E4405F',
      'TikTok': '#000000',
      'YouTube': '#FF0000',
      'Otro': '#757575'
    };
    return colors[network] || '#757575';
  };

  const renderCamposEspecificos = (formData, handleChange) => (
    <div className="form-grid">
      <div className="form-field">
        <label className="required">Red Social</label>
        <select
          value={formData.campos_especificos.network || ''}
          onChange={(e) => handleChange('network', e.target.value)}
          required
        >
          <option value="">Seleccione red social</option>
          {redesSociales.length > 0 ? (
            redesSociales.map(red => (
              <option key={red.id} value={red.value}>{red.value}</option>
            ))
          ) : (
            <>
              <option value="X">X</option>
              <option value="Facebook">Facebook</option>
              <option value="Instagram">Instagram</option>
              <option value="TikTok">TikTok</option>
              <option value="YouTube">YouTube</option>
              <option value="Página WEB">Página WEB</option>
            </>
          )}
        </select>
      </div>
      <div className="form-field">
        <label>Autor o cuenta</label>
        <select
          value={cuentasSociales.some(c => c.value === formData.campos_especificos.author) ? formData.campos_especificos.author : (formData.campos_especificos.author ? '__otro__' : '')}
          onChange={(e) => {
            if (e.target.value === '__otro__') {
              handleChange('author', '');
            } else {
              handleChange('author', e.target.value);
            }
          }}
        >
          <option value="">Seleccione una cuenta</option>
          {cuentasSociales.map(cuenta => (
            <option key={cuenta.id} value={cuenta.value}>{cuenta.value} {cuenta.red_social ? `(${cuenta.red_social})` : ''}</option>
          ))}
          <option value="__otro__">-- Otra cuenta --</option>
        </select>
        {(formData.campos_especificos.author === '' || !cuentasSociales.some(c => c.value === formData.campos_especificos.author)) && formData.campos_especificos.author !== '' ? null : null}
        {(!cuentasSociales.some(c => c.value === formData.campos_especificos.author) || formData.campos_especificos.author === '') && (
          <input
            type="text"
            value={cuentasSociales.some(c => c.value === formData.campos_especificos.author) ? '' : (formData.campos_especificos.author || '')}
            onChange={(e) => handleChange('author', e.target.value)}
            placeholder="Escriba @cuenta"
            style={{ marginTop: '0.5rem' }}
          />
        )}
      </div>
      <div className="form-field">
        <label>Temática relacionada</label>
        <input
          type="text"
          list="tematicas-sugeridas-crear"
          data-testid="crear-tematica"
          value={formData.campos_especificos.tematica || ''}
          onChange={(e) => handleChange('tematica', e.target.value)}
          placeholder="Seleccione o escriba la temática"
        />
        <datalist id="tematicas-sugeridas-crear">
          <option value="Corrupción en Compras y Contrataciones Públicas" />
          <option value="Sobreprecio y Direccionamiento de Contratos" />
          <option value="Uso Ineficiente de Recursos Públicos" />
          <option value="Malversación y Uso Indebido de Fondos Públicos" />
          <option value="Nepotismo" />
          <option value="Conflicto de Interés" />
          <option value="Plazas Fantasma" />
          <option value="Soborno, Cohecho y Extorsión" />
          <option value="Fraude y Falsificación Documental" />
          <option value="Delitos Informáticos" />
          <option value="Incumplimiento de Deberes" />
          <option value="Abuso de Autoridad" />
          <option value="Deficiencias de Control Interno" />
          <option value="Transparencia y Acceso a la Información" />
          <option value="Irregularidades Presupuestarias y Financieras" />
          <option value="Irregularidades en Programas Sociales" />
          <option value="Irregularidades en Subsidios Estatales" />
          <option value="Infraestructura y Obra Pública" />
          <option value="Gestión Deficiente de Proyectos" />
          <option value="Corrupción en Salud Pública" />
          <option value="Corrupción en Educación" />
          <option value="Corrupción en Seguridad y Sistema Penitenciario" />
          <option value="Abuso Policial" />
          <option value="Control y Regulación Deficiente" />
          <option value="Licencias, Permisos y Autorizaciones Irregulares" />
          <option value="Clientelismo y Uso Político de Recursos Públicos" />
          <option value="Enriquecimiento Ilícito" />
          <option value="Tráfico de Influencias" />
          <option value="Redes de Corrupción" />
          <option value="Fiscalización Legislativa" />
          <option value="Investigación Periodística" />
          <option value="Denuncia Ciudadana" />
          <option value="Gestión de Recursos Humanos" />
          <option value="Logística y Administración Institucional" />
          <option value="Seguridad Institucional" />
          <option value="Riesgos de Integridad" />
          <option value="Gobernanza y Rendición de Cuentas" />
          <option value="Cumplimiento Normativo" />
          <option value="Ética Pública" />
          <option value="Otros" />
        </datalist>
      </div>
      <div className="form-field">
        <label>Personas relacionadas</label>
        <input
          type="text"
          value={formData.campos_especificos.persons || ''}
          onChange={(e) => handleChange('persons', e.target.value)}
          placeholder="Personas mencionadas en la publicación"
        />
      </div>
      
      {/* Campo de múltiples enlaces */}
      <div className="form-field" style={{ gridColumn: '1 / -1' }}>
        <label className="required">Enlaces de la publicación</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {/* Lista de enlaces existentes */}
          {(formData.campos_especificos.enlaces || [formData.campos_especificos.link].filter(Boolean)).map((enlace, index) => (
            <div key={index} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <input
                type="url"
                value={enlace || ''}
                onChange={(e) => {
                  const nuevosEnlaces = [...(formData.campos_especificos.enlaces || [formData.campos_especificos.link].filter(Boolean))];
                  nuevosEnlaces[index] = e.target.value;
                  handleChange('enlaces', nuevosEnlaces);
                  // Mantener compatibilidad con link principal
                  if (index === 0) handleChange('link', e.target.value);
                }}
                placeholder="https://..."
                style={{ flex: 1 }}
                required={index === 0}
              />
              {(formData.campos_especificos.enlaces?.length > 1 || index > 0) && (
                <button
                  type="button"
                  onClick={() => {
                    const nuevosEnlaces = (formData.campos_especificos.enlaces || [formData.campos_especificos.link].filter(Boolean)).filter((_, i) => i !== index);
                    handleChange('enlaces', nuevosEnlaces.length > 0 ? nuevosEnlaces : ['']);
                    if (index === 0 && nuevosEnlaces.length > 0) handleChange('link', nuevosEnlaces[0]);
                  }}
                  style={{
                    background: '#FFEBEE',
                    color: '#D32F2F',
                    border: '1px solid #FFCDD2',
                    borderRadius: '4px',
                    padding: '0.4rem 0.6rem',
                    cursor: 'pointer',
                    fontSize: '0.85rem'
                  }}
                  title="Eliminar enlace"
                >
                  ✕
                </button>
              )}
            </div>
          ))}
          
          {/* Botón agregar enlace */}
          <button
            type="button"
            onClick={() => {
              const enlacesActuales = formData.campos_especificos.enlaces || [formData.campos_especificos.link].filter(Boolean);
              // Validar que el último enlace no esté vacío
              if (enlacesActuales.length === 0 || enlacesActuales[enlacesActuales.length - 1]?.trim()) {
                handleChange('enlaces', [...enlacesActuales, '']);
              }
            }}
            style={{
              background: 'linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 100%)',
              color: '#1565C0',
              border: '1px solid #90CAF9',
              borderRadius: '6px',
              padding: '0.5rem 1rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.85rem',
              fontWeight: 500,
              width: 'fit-content'
            }}
          >
            <Plus size={16} />
            Agregar Enlace
          </button>
        </div>
        <small style={{ color: '#666', marginTop: '0.25rem', display: 'block' }}>
          Puede agregar múltiples enlaces relacionados con la publicación
        </small>
      </div>
    </div>
  );

  // Vista de detalle
  if (view === 'detail' && selectedGestion) {
    return (
      <div className="modulo-container">
        <DetalleGestion 
          gestionId={selectedGestion.id} 
          onBack={() => {
            setSelectedGestion(null);
            setView('list');
            fetchGestiones();
          }} 
        />
      </div>
    );
  }

  // Vista de edición
  if (view === 'edit' && selectedGestion) {
    return (
      <div className="modulo-container">
        <EditarGestion 
          gestion={selectedGestion}
          onSuccess={() => {
            setSelectedGestion(null);
            setView('list');
            fetchGestiones();
            toast.success('Gestión actualizada correctamente');
          }}
          onCancel={() => {
            setSelectedGestion(null);
            setView('list');
          }}
        />
      </div>
    );
  }

  return (
    <div className="modulo-container">
      <div className="modulo-header-bar">
        <button className="btn-back" onClick={onBack}>
          <ArrowLeft size={20} />
          Volver a Módulos
        </button>
        
        <div className="modulo-title-section">
          <div className="modulo-icon-header" style={{ backgroundColor: '#2196F3' }}>
            <Newspaper size={24} />
          </div>
          <div>
            <h1 className="modulo-main-title">Monitoreo</h1>
            <p className="modulo-nomenclatura">Nomenclatura: MM-###</p>
          </div>
        </div>
      </div>

      <div className="modulo-tabs">
        <button 
          className={`modulo-tab ${view === 'list' ? 'active' : ''}`}
          onClick={() => setView('list')}
        >
          Listado de Casos
        </button>
        {canCreateCases() && (
          <button 
            className={`modulo-tab ${view === 'create' ? 'active' : ''}`}
            onClick={() => setView('create')}
          >
            <Plus size={16} />
            Nueva Gestión
          </button>
        )}
        {canCreateCases() && (
          <button 
            className={`modulo-tab ${view === 'scraping' ? 'active' : ''}`}
            onClick={() => setView('scraping')}
          >
            <Globe size={16} />
            Web Scraping
          </button>
        )}
        {(user?.role === 'admin' || user?.role === 'editor') && (
          <button 
            className={`modulo-tab ${view === 'grok' ? 'active' : ''}`}
            onClick={() => setView('grok')}
            style={{ background: view === 'grok' ? 'linear-gradient(135deg, #4F46E5, #7C3AED)' : undefined }}
          >
            <Sparkles size={16} />
            Grok AI
          </button>
        )}
        <button 
          className={`modulo-tab ${view === 'stats' ? 'active' : ''}`}
          onClick={() => setView('stats')}
        >
          <BarChart3 size={16} />
          Estadísticas
        </button>
      </div>

      <div className="modulo-content">
        {view === 'list' && (
          <div>
            {/* Filtros Ampliados */}
            <div className="panel filters-compact" style={{ marginBottom: '1.5rem' }}>
              {/* Fila 1: Búsqueda principal y filtros de categoría */}
              <div className="filters-row" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div className="filter-item">
                  <label>Búsqueda General</label>
                  <input
                    type="text"
                    placeholder="Nomenclatura, descripción, personas..."
                    value={filtros.q}
                    onChange={(e) => setFiltros({...filtros, q: e.target.value})}
                  />
                </div>
                <div className="filter-item">
                  <label>Institución</label>
                  <select value={filtros.institucion} onChange={(e) => setFiltros({...filtros, institucion: e.target.value})}>
                    <option value="">Todas</option>
                    {instituciones.map(inst => (
                      <option key={inst.id} value={inst.nombre}>{inst.nombre}</option>
                    ))}
                  </select>
                </div>
                <div className="filter-item">
                  <label>Asesor CNC</label>
                  <select value={filtros.asesor_cnc} onChange={(e) => setFiltros({...filtros, asesor_cnc: e.target.value})}>
                    <option value="">Todos</option>
                    {asesoresCNC.map(asesor => (
                      <option key={asesor.id} value={asesor.nombre_completo}>{asesor.nombre_completo}</option>
                    ))}
                  </select>
                </div>
                <div className="filter-item">
                  <label>Estado</label>
                  <select value={filtros.estado} onChange={(e) => setFiltros({...filtros, estado: e.target.value})}>
                    <option value="">Todos</option>
                    <option value="Registrado">Registrado</option>
                    <option value="Asignada">Asignada</option>
                    <option value="En proceso">En proceso</option>
                    <option value="Enviado / Compartido">Enviado / Compartido</option>
                    <option value="En espera">En espera</option>
                    <option value="En revisión">En revisión</option>
                    <option value="Derivado a probidad">Derivado a probidad</option>
                    <option value="Completada / Archivada">Completada / Archivada</option>
                  </select>
                </div>
              </div>
              
              {/* Fila 2: Filtros específicos del módulo y fechas */}
              <div className="filters-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div className="filter-item">
                  <label>Red Social</label>
                  <select value={filtros.red_social} onChange={(e) => setFiltros({...filtros, red_social: e.target.value})}>
                    <option value="">Todas</option>
                    {redesSociales.length > 0 ? (
                      redesSociales.map(red => (
                        <option key={red.id} value={red.value}>{red.value}</option>
                      ))
                    ) : (
                      <>
                        <option value="X">X</option>
                        <option value="Facebook">Facebook</option>
                        <option value="Instagram">Instagram</option>
                        <option value="TikTok">TikTok</option>
                        <option value="YouTube">YouTube</option>
                        <option value="Página WEB">Página WEB</option>
                      </>
                    )}
                  </select>
                </div>
                <div className="filter-item">
                  <label>Asig. CNC Desde</label>
                  <input type="date" value={filtros.fecha_asignacion_desde} onChange={(e) => setFiltros({...filtros, fecha_asignacion_desde: e.target.value})} />
                </div>
                <div className="filter-item">
                  <label>Asig. CNC Hasta</label>
                  <input type="date" value={filtros.fecha_asignacion_hasta} onChange={(e) => setFiltros({...filtros, fecha_asignacion_hasta: e.target.value})} />
                </div>
                <div className="filter-item">
                  <label>Venc. CNC Desde</label>
                  <input type="date" value={filtros.fecha_vencimiento_cnc_desde} onChange={(e) => setFiltros({...filtros, fecha_vencimiento_cnc_desde: e.target.value})} />
                </div>
                <div className="filter-item">
                  <label>Venc. CNC Hasta</label>
                  <input type="date" value={filtros.fecha_vencimiento_cnc_hasta} onChange={(e) => setFiltros({...filtros, fecha_vencimiento_cnc_hasta: e.target.value})} />
                </div>
                <div className="filter-item">
                  <label>Venc. IP Desde</label>
                  <input type="date" value={filtros.fecha_vencimiento_probidad_desde} onChange={(e) => setFiltros({...filtros, fecha_vencimiento_probidad_desde: e.target.value})} />
                </div>
              </div>
              
              {/* Fila 3: Más filtros de fecha y botones */}
              <div className="filters-row" style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end' }}>
                <div className="filter-item">
                  <label>Venc. IP Hasta</label>
                  <input type="date" value={filtros.fecha_vencimiento_probidad_hasta} onChange={(e) => setFiltros({...filtros, fecha_vencimiento_probidad_hasta: e.target.value})} />
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', marginLeft: 'auto' }}>
                  <button className="btn btn-primary" onClick={handleBuscar}>
                    <Search size={16} />
                    Buscar
                  </button>
                  <button className="btn btn-secondary" onClick={handleLimpiarFiltros}>
                    Limpiar
                  </button>
                </div>
              </div>
            </div>

            {/* Tabla de gestiones */}
            <div className="panel">
              <div className="panel-header">
                <h2>Gestiones de Monitoreo ({gestiones.length})</h2>
                <button className="btn btn-secondary" onClick={handleExportarExcel}>
                  <FileDown size={16} />
                  Exportar Excel
                </button>
              </div>

              {loading ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <div className="loader"></div>
                </div>
              ) : gestiones.length === 0 ? (
                <div className="empty-state">
                  <Newspaper size={48} style={{ opacity: 0.3 }} />
                  <p>No hay gestiones de monitoreo registradas</p>
                  {canCreateCases() && (
                    <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                      <button className="btn btn-primary" onClick={() => setView('create')}>
                        <Plus size={16} />
                        Crear Gestión Manual
                      </button>
                      <button className="btn btn-secondary" onClick={() => setView('scraping')}>
                        <Globe size={16} />
                        Usar Web Scraping
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="table-wrapper">
                  <table className="detail-table">
                    <thead>
                      <tr>
                        <th>Nomenclatura</th>
                        <th>Fecha</th>
                        <th>Autor</th>
                        <th>Red Social</th>
                        <th>Institución</th>
                        <th>Estado</th>
                        <th>Asesor CNC</th>
                        <th>Venc. CNC</th>
                        <th>Probidad</th>
                        <th>Asesor IP</th>
                        <th>Venc. IP</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {gestiones.map((gestion) => {
                        const hoy = new Date();
                        hoy.setHours(0, 0, 0, 0);
                        const fechaVencCNC = gestion.fecha_vencimiento ? parseLocalDate(gestion.fecha_vencimiento) : null;
                        const fechaVencIP = gestion.fecha_vencimiento_probidad ? parseLocalDate(gestion.fecha_vencimiento_probidad) : null;
                        // Si ya fue derivada a Probidad, NO se considera vencido para CNC
                        const yaDerivadaAProbidad = gestion.asesor_probidad_id || gestion.asesor_probidad_nombre;
                        // Una gestión está VENCIDA a partir del DÍA SIGUIENTE a la fecha de vencimiento
                        const vencidoCNC = fechaVencCNC && hoy > fechaVencCNC && gestion.estado !== 'Completada / Archivada' && !yaDerivadaAProbidad;
                        const atendidoEnTiempoCNC = fechaVencCNC && hoy > fechaVencCNC && yaDerivadaAProbidad && gestion.estado !== 'Completada / Archivada';
                        const vencidoIP = fechaVencIP && hoy > fechaVencIP && gestion.estado !== 'Completada / Archivada';
                        
                        return (
                          <tr key={gestion.id}>
                            <td><strong>{gestion.nomenclatura}</strong></td>
                            <td>{formatLocalDate(gestion.fecha_asignacion)}</td>
                            <td>{gestion.campos_especificos?.author || 'N/A'}</td>
                            <td>
                              <span 
                                className="badge" 
                                style={{ 
                                  backgroundColor: getRedSocialBadge(gestion.campos_especificos?.network),
                                  color: 'white'
                                }}
                              >
                                {gestion.campos_especificos?.network || 'N/A'}
                              </span>
                            </td>
                            <td>{gestion.institucion}</td>
                            <td>
                              <span className={`badge ${gestion.estado === 'Completada / Archivada' ? 'badge-success' : gestion.estado === 'En proceso de análisis' ? 'badge-info' : 'badge-warning'}`}>
                                {gestion.estado}
                              </span>
                            </td>
                            <td>{gestion.nombre_asesor_cnc}</td>
                            <td style={{ color: vencidoCNC ? '#D32F2F' : atendidoEnTiempoCNC ? '#2E7D32' : 'inherit', fontWeight: (vencidoCNC || atendidoEnTiempoCNC) ? 600 : 'normal' }}>
                              {formatLocalDate(gestion.fecha_vencimiento)}
                              {vencidoCNC && <AlertTriangle size={12} style={{ marginLeft: '4px', color: '#D32F2F' }} />}
                              {atendidoEnTiempoCNC && <span style={{ marginLeft: '4px', color: '#2E7D32', fontSize: '0.7rem' }}>✓</span>}
                            </td>
                            <td>
                              {gestion.asesor_probidad_id ? (
                                <span className="badge badge-success" style={{ fontSize: '0.65rem' }}>Sí</span>
                              ) : (
                                <span className="badge badge-secondary" style={{ fontSize: '0.65rem' }}>No</span>
                              )}
                            </td>
                            <td style={{ fontSize: '0.8rem' }}>{gestion.asesor_probidad_nombre || '-'}</td>
                            <td style={{ color: vencidoIP ? '#D32F2F' : 'inherit', fontWeight: vencidoIP ? 600 : 'normal' }}>
                              {formatLocalDate(gestion.fecha_vencimiento_probidad)}
                              {vencidoIP && <AlertTriangle size={12} style={{ marginLeft: '4px', color: '#D32F2F' }} />}
                            </td>
                            <td>
                              <AccionesGestion 
                                gestion={gestion}
                                onView={handleViewDetail}
                                onEdit={canEditCases() ? handleEditGestion : null}
                                compact={true}
                                canEdit={canEditCases()}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {view === 'create' && (
          <FormularioGestion
            tipoGestion="Monitoreo"
            nomenclatura="MM"
            camposEspecificos={renderCamposEspecificos}
            onSuccess={() => {
              fetchGestiones();
              setView('list');
            }}
            onCancel={() => setView('list')}
          />
        )}

        {view === 'scraping' && (
          <div>
            <div className="panel" style={{ marginBottom: '1rem', padding: '1rem' }}>
              <p style={{ margin: 0, color: 'var(--color-text-muted)' }}>
                <strong>Nota:</strong> Al admitir un resultado, se abrirá el formulario de creación con los datos prellenados. Complete los campos faltantes y guarde la gestión.
              </p>
            </div>
            <WebScraping 
              onPostCreated={fetchGestiones}
              onAdmitResult={(datos) => {
                setDatosPrellenos(datos);
                setView('create-from-scraping');
              }}
            />
          </div>
        )}

        {view === 'create-from-scraping' && datosPrellenos && (
          <FormularioGestionPrelleno
            tipoGestion="Monitoreo"
            nomenclatura="MM"
            datosIniciales={datosPrellenos}
            camposEspecificos={renderCamposEspecificos}
            onSuccess={() => {
              // Marcar el resultado como admitido
              if (datosPrellenos.scraping_result_id) {
                const token = localStorage.getItem('token');
                axios.post(
                  `${API}/scraping/mark-admitted/${datosPrellenos.scraping_result_id}`,
                  {},
                  { headers: { Authorization: `Bearer ${token}` } }
                ).catch(err => console.log('Resultado marcado como admitido'));
              }
              setDatosPrellenos(null);
              fetchGestiones();
              setView('list');
              toast.success('Gestión creada correctamente desde Web Scraping');
            }}
            onCancel={() => {
              setDatosPrellenos(null);
              setView('scraping');
            }}
          />
        )}

        {view === 'grok' && (
          <div className="grok-ai-container" style={{
            background: 'linear-gradient(180deg, #EEF2FF 0%, #E0E7FF 100%)',
            borderRadius: '16px',
            padding: '3rem',
            textAlign: 'center',
            minHeight: '600px'
          }}>
            {/* Header */}
            <div style={{ marginBottom: '2rem' }}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '1rem',
                marginBottom: '1rem'
              }}>
                <div style={{
                  background: 'linear-gradient(135deg, #4F46E5, #7C3AED)',
                  borderRadius: '16px',
                  padding: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Sparkles size={32} color="white" />
                </div>
                <h1 style={{ 
                  fontSize: '2.5rem', 
                  fontWeight: 700, 
                  color: '#1E1B4B',
                  margin: 0
                }}>
                  Grok AI
                </h1>
              </div>
              <p style={{ 
                fontSize: '1.25rem', 
                color: '#4338CA',
                fontWeight: 500,
                marginBottom: '0.5rem'
              }}>
                Búsqueda de Noticias con Inteligencia Artificial
              </p>
              <p style={{ 
                fontSize: '1rem', 
                color: '#6366F1',
                maxWidth: '600px',
                margin: '0 auto 2rem',
                lineHeight: 1.6
              }}>
                Grok AI es una IA avanzada que te ayuda a encontrar, analizar y comprender 
                noticias y publicaciones relevantes en tiempo real.
              </p>
              
              {/* Botón principal */}
              <a
                href="https://x.com/i/grok"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  background: 'linear-gradient(135deg, #4F46E5, #7C3AED)',
                  color: 'white',
                  padding: '1rem 2rem',
                  borderRadius: '12px',
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  textDecoration: 'none',
                  boxShadow: '0 4px 20px rgba(79, 70, 229, 0.4)',
                  transition: 'transform 0.2s, box-shadow 0.2s'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 25px rgba(79, 70, 229, 0.5)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 20px rgba(79, 70, 229, 0.4)';
                }}
              >
                <Sparkles size={20} />
                Abrir Grok AI
                <span style={{ marginLeft: '0.25rem' }}>→</span>
              </a>
            </div>

            {/* Feature Cards */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: '1.5rem',
              maxWidth: '1000px',
              margin: '0 auto'
            }}>
              {/* Card 1: Búsqueda Inteligente */}
              <div style={{
                background: 'white',
                borderRadius: '16px',
                padding: '1.5rem',
                boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
                textAlign: 'center'
              }}>
                <div style={{
                  background: '#EEF2FF',
                  borderRadius: '12px',
                  width: '56px',
                  height: '56px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1rem'
                }}>
                  <Search size={28} color="#4F46E5" />
                </div>
                <h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#1E1B4B', marginBottom: '0.5rem' }}>
                  Búsqueda Inteligente
                </h3>
                <p style={{ fontSize: '0.85rem', color: '#6B7280', lineHeight: 1.5 }}>
                  Encuentra noticias y publicaciones relevantes con búsquedas en lenguaje natural
                </p>
              </div>

              {/* Card 2: Análisis Profundo */}
              <div style={{
                background: 'white',
                borderRadius: '16px',
                padding: '1.5rem',
                boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
                textAlign: 'center'
              }}>
                <div style={{
                  background: '#EEF2FF',
                  borderRadius: '12px',
                  width: '56px',
                  height: '56px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1rem'
                }}>
                  <Brain size={28} color="#4F46E5" />
                </div>
                <h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#1E1B4B', marginBottom: '0.5rem' }}>
                  Análisis Profundo
                </h3>
                <p style={{ fontSize: '0.85rem', color: '#6B7280', lineHeight: 1.5 }}>
                  Análisis de contexto sobre corrupción y transparencia institucional
                </p>
              </div>

              {/* Card 3: Tendencias en Tiempo Real */}
              <div style={{
                background: 'white',
                borderRadius: '16px',
                padding: '1.5rem',
                boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
                textAlign: 'center'
              }}>
                <div style={{
                  background: '#EEF2FF',
                  borderRadius: '12px',
                  width: '56px',
                  height: '56px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1rem'
                }}>
                  <TrendingUp size={28} color="#4F46E5" />
                </div>
                <h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#1E1B4B', marginBottom: '0.5rem' }}>
                  Tendencias en Tiempo Real
                </h3>
                <p style={{ fontSize: '0.85rem', color: '#6B7280', lineHeight: 1.5 }}>
                  Identifica tendencias y temas emergentes en redes sociales
                </p>
              </div>

              {/* Card 4: Respuestas Instantáneas */}
              <div style={{
                background: 'white',
                borderRadius: '16px',
                padding: '1.5rem',
                boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
                textAlign: 'center'
              }}>
                <div style={{
                  background: '#EEF2FF',
                  borderRadius: '12px',
                  width: '56px',
                  height: '56px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1rem'
                }}>
                  <Zap size={28} color="#4F46E5" />
                </div>
                <h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#1E1B4B', marginBottom: '0.5rem' }}>
                  Respuestas Instantáneas
                </h3>
                <p style={{ fontSize: '0.85rem', color: '#6B7280', lineHeight: 1.5 }}>
                  Obtén respuestas inmediatas a tus consultas de monitoreo
                </p>
              </div>
            </div>

            {/* Footer note */}
            <p style={{ 
              marginTop: '2rem', 
              fontSize: '0.85rem', 
              color: '#9CA3AF',
              maxWidth: '600px',
              margin: '2rem auto 0'
            }}>
              Grok AI es una herramienta de X (Twitter) que utiliza inteligencia artificial 
              para búsqueda y análisis de información en tiempo real.
            </p>
          </div>
        )}

        {view === 'stats' && (
          <EstadisticasModulo 
            tipoGestion="Monitoreo" 
            colorModulo="#2196F3"
          />
        )}
      </div>
    </div>
  );
};

export default ModuloMonitoreoMedios;
